import { useId } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal } from '@shared/components';
import { DealerCollectedPaymentForm } from './steps/PaymentStep/DealerCollectedPaymentForm';
import { PaymentLinkDeliveryFields } from './steps/PaymentStep/PaymentLinkDeliveryFields';
import { paymentCollectionCopy } from './steps/PaymentStep/copy';
import {
    buildDealerCollectedPaymentSchema,
    type DealerCollectedPaymentFormValues,
} from './steps/PaymentStep/dealerCollectedPayment.schema';
import { todayDateKey } from '../utils/dateKey';
import type { CollectionRouting, DealerCollectedDeposit } from '../types/schedule.types';
import type { CustomerLinkContacts, LinkDeliveryChoice } from '../types/linkDelivery.types';
import {
    actionForRouting,
    confirmedLinkDelivery,
    dealerRoutingFor,
    defaultLinkDelivery,
    elemisRoutingFor,
    offersLinkDelivery,
    type PaymentCollectionAction,
    type PaymentCollectionModalMode,
} from '../utils/paymentCollectionGate';

type OpenMode = Exclude<PaymentCollectionModalMode, { kind: 'none' }>;

interface PaymentCollectionModalProps {
    isOpen: boolean;
    mode: OpenMode;
    currency: string;
    contacts: CustomerLinkContacts;
    /** A checkout is already held: Continue reopens it, so no link can be sent from here. */
    liveCheckoutHeld: boolean;
    /** True while the confirmed choice is being saved and acted on; the controls lock. */
    isBusy?: boolean;
    /** The signed-in user's name, named in the Direct-to-Dealer confirmation attestation. */
    userName?: string;
    /** The customer's title and name, named in the Direct-to-Dealer confirmation attestation. */
    customerName?: string;
    onCancel: () => void;
    onConfirm: (
        routing: CollectionRouting | undefined,
        action: PaymentCollectionAction,
        delivery: LinkDeliveryChoice,
    ) => void;
}

const defaultValues = (mode: OpenMode, contacts: CustomerLinkContacts): DealerCollectedPaymentFormValues => ({
    collectedBy: mode.kind === 'dealer_only' ? 'dealer' : 'elemis',
    reference: '',
    collectedAt: todayDateKey(),
    confirmed: false,
    ...defaultLinkDelivery(contacts),
});

const toDealerPayment = (
    values: DealerCollectedPaymentFormValues,
    amountMinor: number,
): DealerCollectedDeposit => ({
    amountMinor,
    reference: values.reference.trim(),
    collectedAt: values.collectedAt,
});

/** The body mounts fresh on each open, so no reset effect is needed. */
export const PaymentCollectionModal: React.FC<PaymentCollectionModalProps> = ({
    isOpen,
    onCancel,
    ...bodyProps
}) => {
    const headingId = useId();

    return (
        <Modal
            isOpen={isOpen}
            onClose={onCancel}
            ariaLabelledBy={headingId}
            showCloseButton={false}
            className="max-w-lg p-6"
        >
            <PaymentCollectionModalBody headingId={headingId} onCancel={onCancel} {...bodyProps} />
        </Modal>
    );
};

function PaymentCollectionModalBody({
    headingId,
    mode,
    currency,
    contacts,
    liveCheckoutHeld,
    isBusy = false,
    userName,
    customerName,
    onCancel,
    onConfirm,
}: Omit<PaymentCollectionModalProps, 'isOpen'> & { headingId: string }) {
    const { control, handleSubmit, getValues } = useForm<DealerCollectedPaymentFormValues>({
        resolver: zodResolver(buildDealerCollectedPaymentSchema()),
        mode: 'onChange',
        defaultValues: defaultValues(mode, contacts),
    });

    const collectedBy = useWatch({ control, name: 'collectedBy' });
    const confirmed = useWatch({ control, name: 'confirmed' });
    const showsDeliveryFields = offersLinkDelivery(mode, collectedBy, liveCheckoutHeld);
    const hasCollectionForm = mode.kind === 'choice' || mode.kind === 'dealer_only';

    const currentDelivery = (): LinkDeliveryChoice => {
        const { sendViaEmail, sendViaSms } = getValues();
        return confirmedLinkDelivery(showsDeliveryFields, { sendViaEmail, sendViaSms }, contacts);
    };

    const confirmDealer = handleSubmit((values) => {
        if (!hasCollectionForm) {
            return;
        }
        const routing = dealerRoutingFor(mode.axis, toDealerPayment(values, mode.amountMinor));
        onConfirm(routing, actionForRouting(mode.axis, routing), currentDelivery());
    });

    const handleContinue = () => {
        if (hasCollectionForm && collectedBy === 'dealer') {
            void confirmDealer();
            return;
        }
        const axis = mode.kind === 'dealer_only' ? 'full_payment' : mode.axis;
        onConfirm(axis === null ? undefined : elemisRoutingFor(axis), 'checkout', currentDelivery());
    };

    const continueDisabled = isBusy || (hasCollectionForm && collectedBy === 'dealer' && !confirmed);

    return (
        <div className="space-y-5">
            <h2 id={headingId} className="text-lg font-semibold text-mp-foreground">
                Confirm payment collection
            </h2>

            {hasCollectionForm ? (
                <DealerCollectedPaymentForm
                    axis={mode.axis}
                    control={control}
                    amountMinor={mode.amountMinor}
                    isDealerOptionOffered
                    isElemisOptionOffered={mode.kind === 'choice'}
                    currency={currency}
                    userName={userName}
                    customerName={customerName}
                />
            ) : (
                <p className="text-sm text-mp-foreground">
                    {paymentCollectionCopy({ kind: 'motorplan_only' }).note}
                </p>
            )}

            {showsDeliveryFields && <PaymentLinkDeliveryFields control={control} contacts={contacts} />}

            <div className="flex justify-end gap-3 border-t border-mp-border pt-4">
                <Button variant="outline" size="sm" onClick={onCancel} disabled={isBusy}>
                    Cancel
                </Button>
                <Button size="sm" onClick={handleContinue} disabled={continueDisabled}>
                    {isBusy ? 'Continuing…' : 'Continue'}
                </Button>
            </div>
        </div>
    );
}
